import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Download, GripHorizontal, Trash2, UserRound, X } from 'lucide-react';

import { useChat } from '../../context/ChatContext';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';
import { formatChatTranscript } from '../../utils/chatTranscript';
import useIsMobile from '../../hooks/useIsMobile';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';

// components/layout's mobile breakpoint (layout.css: `@media (max-width:
// 820px)`) — chatbot.css switches the panel to a full-screen sheet at the
// same width, so drag needs to turn off in lockstep with it (Phase 11).
const MOBILE_BREAKPOINT = 821;
const EDGE_MARGIN = 8;
const DRAG_IGNORE_SELECTOR =
  '.ss-chat-panel__close, .ss-chat-panel__clear, .ss-chat-panel__copy, .ss-chat-panel__download';

// Slide-over panel, mounted once by ChatWidget; visibility from ChatContext.isOpen
// (mirrors CartDrawer's relationship to CartContext). Draggable by its header
// so it can be moved off the default bottom-right corner — the fixed spot
// otherwise sits on top of page content the user still needs to reach.
export default function ChatPanel() {
  const { isOpen, closeWidget, messages, clearConversation, closeConversation, conversationMode } = useChat();
  const { confirm, dialog } = useConfirm();
  const { addToast } = useToast();
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const [pos, setPos] = useState(null); // null = default CSS-anchored bottom-right
  const [dragging, setDragging] = useState(false);
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);

  const onClear = async () => {
    const ok = await confirm({
      title: 'Delete conversation',
      message: 'This will delete your chat history and start a new conversation.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) clearConversation();
  };

  // Closing always closes — the question is whether there's a conversation
  // worth ending first. "Close" here (closeConversation) marks the
  // conversation done server-side but keeps its history — distinct from the
  // trash icon's onClear above, which deletes it outright. Either way, the
  // next message after closing starts a brand new conversation.
  const onCloseAttempt = useCallback(async () => {
    if (messages.length > 0) {
      const ok = await confirm({
        title: 'Close this conversation?',
        message: "You'll start a new conversation next time you open chat. This one is saved for reference.",
        confirmLabel: 'Close chat',
        cancelLabel: 'Keep chatting',
      });
      if (ok) closeConversation();
    }
    closeWidget();
  }, [messages.length, confirm, closeConversation, closeWidget]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatChatTranscript(messages));
      addToast('Conversation copied to clipboard', { variant: 'success' });
    } catch {
      addToast('Could not copy the conversation', { variant: 'error' });
    }
  };

  const onDownload = () => {
    const blob = new Blob([formatChatTranscript(messages)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sustainably-sprouted-chat-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseAttempt();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCloseAttempt]);

  const clampToViewport = useCallback((top, left) => {
    const el = panelRef.current;
    const w = el?.offsetWidth || 0;
    const h = el?.offsetHeight || 0;
    const maxLeft = Math.max(window.innerWidth - w - EDGE_MARGIN, EDGE_MARGIN);
    const maxTop = Math.max(window.innerHeight - h - EDGE_MARGIN, EDGE_MARGIN);
    return {
      top: Math.min(Math.max(top, EDGE_MARGIN), maxTop),
      left: Math.min(Math.max(left, EDGE_MARGIN), maxLeft),
    };
  }, []);

  // Keep it on-screen if the viewport is resized (e.g. rotating a phone) after a drag.
  useEffect(() => {
    if (!pos) return undefined;
    const onResize = () => setPos((p) => (p ? clampToViewport(p.top, p.left) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pos, clampToViewport]);

  // A drag position from a wider viewport is a set of pixel top/left values
  // (see `style` below) — left in place, it would fight the full-screen
  // sheet chatbot.css switches to at this same breakpoint, so drop back to
  // the CSS-anchored default the moment the viewport crosses into mobile.
  useEffect(() => {
    if (isMobile) setPos(null);
  }, [isMobile]);

  const onPointerDown = (e) => {
    if (isMobile) return; // full-screen sheet on mobile — nothing to drag
    if (e.target.closest(DRAG_IGNORE_SELECTOR)) return; // let header buttons work normally
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startTop: rect.top, startLeft: rect.left };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const { startX, startY, startTop, startLeft } = dragRef.current;
    setPos(clampToViewport(startTop + (e.clientY - startY), startLeft + (e.clientX - startX)));
  };

  const onPointerUp = (e) => {
    dragRef.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const style = pos ? { top: pos.top, left: pos.left, right: 'auto', bottom: 'auto' } : undefined;

  return (
    <aside
      ref={panelRef}
      className={`ss-chat-panel ${isOpen ? 'is-open' : ''}`}
      style={style}
      role="dialog"
      aria-modal="true"
      aria-label="Chat with us"
      aria-hidden={!isOpen}
    >
      <div
        className={`ss-chat-panel__head ${dragging ? 'is-dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <GripHorizontal size={16} className="ss-chat-panel__grip" aria-hidden="true" />
        <h2>Chat with us</h2>
        {messages.length > 0 && (
          <>
            <button type="button" className="ss-chat-panel__copy" aria-label="Copy conversation" onClick={onCopy}>
              <Copy size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ss-chat-panel__download"
              aria-label="Download conversation"
              onClick={onDownload}
            >
              <Download size={18} aria-hidden="true" />
            </button>
            <button type="button" className="ss-chat-panel__clear" aria-label="Clear conversation" onClick={onClear}>
              <Trash2 size={18} aria-hidden="true" />
            </button>
          </>
        )}
        <button type="button" className="ss-chat-panel__close" aria-label="Close chat" onClick={onCloseAttempt}>
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      {conversationMode === 'human' && (
        <div className="ss-chat-human-banner">
          <UserRound size={14} aria-hidden="true" />
          <span>You&rsquo;re now talking with a person</span>
        </div>
      )}

      <ChatMessageList />
      <ChatInput />
      {dialog}
    </aside>
  );
}
